import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useLayoutContext } from '../../../Context/Layout.context';
import { AnnotationDefinition, AnnotationType } from '../../../models/annotation.types';
import {
  AnnotationDefAddResult,
  createAnnotationDefinition,
  updateAnnotationDefinition,
} from '../../../services/admin/settings/annotations.service';
import AnnotationPreview from '../../AnnotationPreview/AnnotationPreview.component';
import { ModalWithButtons } from '../../ModalWithButtons/ModalWithButtons.component';
import { LeavePrompt } from '../../Prompt/LeavePrompt/LeavePrompt.component';
import { useStyles } from '../SystemSettings.styles';

const NAME_MAX_LENGTH = 105;
const DESCRIPTION_MAX_LENGTH = 256;
const SVG_MAX_LENGTH = 4096;

interface AnnotationDefinitionDetailsProps {
  title: string;
  open: boolean;
  annotationDefinition?: AnnotationDefinition;
  onClose: () => void;
  onSave: () => void;
}

export const AnnotationDefinitionDetails: React.FC<AnnotationDefinitionDetailsProps> = (props) => {
  const classes = useStyles();

  const { openAlert, showLoader } = useLayoutContext();

  const [svgCodeInput, setSvgCodeInput] = useState<string>(
    props.annotationDefinition !== undefined ? props.annotationDefinition.svg : '',
  );
  const [svgCode, setSvgCode] = useState<string>(
    props.annotationDefinition !== undefined ? props.annotationDefinition.svg : '',
  );
  const [type, setType] = useState<AnnotationType>(
    props.annotationDefinition !== undefined ? props.annotationDefinition.annotationType : AnnotationType.static,
  );
  const [repetitive, setRepetitive] = useState<boolean>(
    props.annotationDefinition !== undefined ? props.annotationDefinition.repetitive : false,
  );
  const [name, setName] = useState<string>(
    props.annotationDefinition !== undefined ? props.annotationDefinition.name : '',
  );
  const [description, setDescription] = useState<string>(
    props.annotationDefinition !== undefined ? props.annotationDefinition.description : '',
  );

  const [leavePromptOpen, setLeavePromptOpen] = useState<boolean>(false);

  const [isSvgInvalid, setSvgInvalid] = useState<boolean>(false);

  const [isSvgDuplicate, setIsSvgDuplicate] = useState<boolean>(false);
  const [isNameDuplicate, setIsNameDuplicate] = useState<boolean>(false);

  const isSVGTooLong = svgCode.length > SVG_MAX_LENGTH;
  const isNameTooLong = name.length > NAME_MAX_LENGTH;
  const isDescTooLong = description.length > DESCRIPTION_MAX_LENGTH;

  const isAnnotationDefinitionModified =
    props.annotationDefinition !== undefined
      ? svgCodeInput !== props.annotationDefinition.svg ||
        type !== props.annotationDefinition.annotationType ||
        repetitive !== props.annotationDefinition.repetitive ||
        name !== props.annotationDefinition.name ||
        description !== props.annotationDefinition.description
      : [svgCodeInput, name, description].findIndex((value) => value !== '') !== -1;

  useEffect(() => {
    if (svgCodeInput !== '') {
      if (svgCodeInput.indexOf('script') !== -1) {
        setSvgInvalid(true);
        setSvgCode('');
      } else {
        setSvgInvalid(false);
        setSvgCode(svgCodeInput.replace(RegExp('/"/gm'), `\"`));
      }
    } else {
      setSvgInvalid(false);
      setSvgCode('');
    }
  }, [svgCodeInput]);

  useEffect(() => {
    if (type === AnnotationType.static && repetitive) {
      setRepetitive(false);
    }
  }, [type, repetitive]);

  const getSvgCodeHelperText = () => {
    if (isSVGTooLong) return 'Podany kod jest za długi';
    if (isSvgInvalid) return 'Wystąpił błąd przy przetwarzaniu kodu SVG';
    if (isSvgDuplicate) return 'Wprowadzony kod SVG istnieje już w systemie';
  };

  const getNameHelperText = () => {
    if (isNameTooLong) return 'Podana nazwa jest za długa';
    if (isNameDuplicate) return 'Wprowadzona nazwa istnieje już w systemie';
  };

  const getDescHelperText = () => {
    if (isDescTooLong) return 'Podany opis jest za długi';
  };

  const isFieldEmpty = (fieldValue: string) => {
    return fieldValue === '';
  };

  const clearFields = () => {
    setSvgCodeInput('');
    setSvgCode('');
    setType(AnnotationType.static);
    setRepetitive(false);
    setName('');
    setDescription('');
    setSvgInvalid(false);
    setIsSvgDuplicate(false);
    setIsNameDuplicate(false);
  };

  const processError = (result: AnnotationDefAddResult) => {
    switch (result) {
      case AnnotationDefAddResult.IconAlreadyExists:
        setIsSvgDuplicate(true);
        break;
      case AnnotationDefAddResult.NameAlreadyExists:
        setIsNameDuplicate(true);
        break;
      case AnnotationDefAddResult.Fail:
      default:
        openAlert('error');
        break;
    }
  };

  const processResult = (result: AnnotationDefAddResult) => {
    if (result === AnnotationDefAddResult.Success) {
      openAlert('success');
      clearFields();
      props.onSave();
    } else processError(result);
  };

  const onFinally = () => showLoader(false);

  const handleOnConfirm = () => {
    showLoader(true);
    if (props.annotationDefinition !== undefined) {
      updateAnnotationDefinition({
        id: props.annotationDefinition.id,
        name: name,
        description: description,
        svg: svgCode,
        annotationType: type,
        active: props.annotationDefinition.active,
        repetitive: repetitive,
      })
        .then(processResult, (error) => {
          processError(error.response.data);
        })
        .catch(() => openAlert('save_error'))
        .finally(onFinally);
    } else {
      createAnnotationDefinition(name, description, svgCode, type, repetitive)
        .then(processResult, (error) => {
          processError(error.response.data);
        })
        .catch(() => openAlert('save_error'))
        .finally(onFinally);
    }
  };

  const typesStyle = {
    '&.Mui-checked': {
      color: '#3C419A',
    },
  };

  const closeComponent = () => {
    clearFields();
    props.onClose();
  };

  const handleClosure = () => {
    if (isAnnotationDefinitionModified) {
      setLeavePromptOpen(true);
    } else closeComponent();
  };

  return (
    <>
      <ModalWithButtons
        open={props.open}
        onCancel={handleClosure}
        xCloseButton={handleClosure}
        confirmButtonDisabled={
          isFieldEmpty(svgCodeInput) ||
          isFieldEmpty(name) ||
          isFieldEmpty(description) ||
          isSvgInvalid ||
          isSVGTooLong ||
          isNameTooLong ||
          isDescTooLong ||
          isSvgDuplicate ||
          isNameDuplicate ||
          !isAnnotationDefinitionModified
        }
        onConfirm={handleOnConfirm}
        title={props.title}
        autoHeight
      >
        <Grid container className={classes.paddedMasterContainer}>
          <Grid container className={classes.paddedGridContainer}>
            <Grid item xs={12} className={classes.paddedGridItem}>
              <Typography>Ikona adnotacji*</Typography>
            </Grid>
            <Grid container>
              <Grid item xs={1}>
                <AnnotationPreview svg={svgCode} onPreviewError={() => setSvgInvalid(true)} />
              </Grid>
              <Grid item xs={11}>
                <TextField
                  className={classes.svgTextField}
                  multiline
                  label="Kod SVG"
                  variant="outlined"
                  fullWidth
                  minRows={5}
                  maxRows={5}
                  value={svgCodeInput}
                  onChange={(e) => {
                    setIsSvgDuplicate(false);
                    setSvgCodeInput(e.target.value);
                  }}
                  color="primary"
                  error={isSVGTooLong || isSvgInvalid || isSvgDuplicate}
                  helperText={getSvgCodeHelperText()}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid container className={classes.paddedGridContainer}>
            <Grid item xs={12} className={classes.paddedGridItem}>
              <Typography>Typ*</Typography>
            </Grid>
            <Grid container className={classes.paddedGridItem}>
              <RadioGroup
                row
                aria-label="wybór typu adnotacji"
                name="annotation-type-selector"
                value={type}
                onChange={(e) => {
                  setType(+e.target.value);
                }}
              >
                <FormControlLabel value={AnnotationType.static} control={<Radio sx={typesStyle} />} label="Statyczna" />
                <FormControlLabel
                  style={{ borderLeft: '1px solid #2C2D3E4D' }}
                  value={AnnotationType.dynamic}
                  control={<Radio sx={typesStyle} />}
                  label="Dynamiczna"
                />
                {type === AnnotationType.dynamic && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        disableRipple
                        checked={repetitive}
                        onChange={(e) => {
                          setRepetitive(e.target.checked);
                        }}
                        sx={typesStyle}
                      />
                    }
                    label="Powtarzalność"
                  />
                )}
              </RadioGroup>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item className={classes.paddedGridItem}>
              <Typography>Nazwa i opis*</Typography>
            </Grid>
            <Grid item xs={12} className={classes.paddedGridItem}>
              <TextField
                className={classes.nameTextField}
                label="Nazwa adnotacji"
                variant="outlined"
                fullWidth
                value={name}
                onChange={(e) => {
                  setIsNameDuplicate(false);
                  setName(e.target.value);
                }}
                color="primary"
                error={isNameTooLong || isNameDuplicate}
                helperText={getNameHelperText()}
              />
            </Grid>
            <Grid item xs={12} className={classes.paddedGridItem}>
              <TextField
                className={classes.descTextField}
                multiline
                label="Opis"
                variant="outlined"
                fullWidth
                minRows={4}
                maxRows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                color="primary"
                error={isDescTooLong}
                helperText={getDescHelperText()}
              />
            </Grid>
          </Grid>
        </Grid>
      </ModalWithButtons>
      <LeavePrompt
        isOpen={leavePromptOpen}
        onConfirm={() => {
          setLeavePromptOpen(false);
          closeComponent();
        }}
        onCancel={() => {
          setLeavePromptOpen(false);
        }}
      />
    </>
  );
};
